// The club's hunting area from Oma riista on the stand board: the area outline and the club's points (stands,
// feeding places, mineral licks). The phone asks our server (server/omariista.ts), which asks Oma riista and keeps a
// shared copy for 24 h. Oma riista wants to know who asked, so this needs a ReelHunt account.
import { accountApi, signedIn } from "../account.ts";
import { OMARIISTA_OPEN } from "../config.ts";
import { lsGet, lsSet } from "../util.ts";

if (new URLSearchParams(location.search).get("omariista") === "1") lsSet("omariista", "1");
/** Whether the "show the club area" link is shown (see OMARIISTA_OPEN). A board that already has an area always shows it. */
export const areaOffered = (): boolean => OMARIISTA_OPEN || location.hostname === "localhost" || lsGet("omariista", "") === "1";

export interface AreaPoint { name: string; set: string; type: string; notes: string; lat: number; lon: number }
export interface ClubArea { name: string; club: string; year: number | null; geo: GeoJSON.Feature; points: AreaPoint[] }
export type AreaResult = { area: ClubArea } | { error: "signin" | "notfound" | "unavailable" | "offline" };

/** Area identifiers are at least 10 letters and digits (the same check as on the server). */
export const AREA_ID_RE = /^[A-Za-z0-9]{10,40}$/;

interface Props { poiType?: string; name?: string | { finnish?: string }; areaName?: { finnish?: string }; clubName?: { finnish?: string }; huntingYear?: number;
  pointProperties?: { name?: string | null; notes?: string | null }[] }

/** Splits Oma riista's GeoJSON into the area outline and the points (one MultiPoint per point set). */
export function parseArea(fc: GeoJSON.FeatureCollection): ClubArea | null {
  const poly = fc.features.find((f) => f.geometry?.type === "Polygon" || f.geometry?.type === "MultiPolygon");
  if (!poly) return null;
  const p = (poly.properties ?? {}) as Props;
  const points: AreaPoint[] = [];
  for (const f of fc.features) {
    if (f.geometry?.type !== "MultiPoint" && f.geometry?.type !== "Point") continue;
    const q = (f.properties ?? {}) as Props, set = typeof q.name === "string" ? q.name : "";
    const coords = f.geometry.type === "Point" ? [f.geometry.coordinates] : f.geometry.coordinates;
    coords.forEach(([lon, lat], i) => {
      const pp = q.pointProperties?.[i];
      points.push({ name: pp?.name ?? "", set, type: q.poiType ?? "OTHER", notes: pp?.notes ?? "", lat, lon });
    });
  }
  return { name: p.areaName?.finnish ?? "", club: p.clubName?.finnish ?? "", year: p.huntingYear ?? null, geo: poly, points };
}

/** A stand's name from an Oma riista point: "Penan passi 1-1", or just the point's own name when it already says more. */
export const pointLabel = (p: AreaPoint): string => (!p.name ? p.set : !p.set || /[a-zåäö]{3}/i.test(p.name) ? p.name : `${p.set} ${p.name}`);

// kept for this session; the server holds the shared copy
const mem = new Map<string, Promise<AreaResult>>();
export function loadClubArea(id: string, again = false): Promise<AreaResult> {
  if (!signedIn()) return Promise.resolve({ error: "signin" });
  let p = mem.get(id);
  if (!p || again) {
    p = (async (): Promise<AreaResult> => {
      if (!navigator.onLine) return { error: "offline" };
      try {
        const res = await accountApi("/api/omariista/area/" + encodeURIComponent(id));
        if (res.status === 401) return { error: "signin" };
        if (res.status === 404) return { error: "notfound" };
        if (!res.ok) return { error: "unavailable" };
        const area = parseArea(((await res.json()) as { area: GeoJSON.FeatureCollection }).area);
        return area ? { area } : { error: "notfound" };
      } catch { return { error: "unavailable" }; }
    })();
    mem.set(id, p);
    // failures are tried again next time
    void p.then((r) => { if ("error" in r) mem.delete(id); });
  }
  return p;
}
